import {Card, Tooltip} from 'platform/components';
import {Box, Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {isEmpty, isNotEmpty, prop, uniqBy} from 'ramda';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps, useBoolean} from 'shared/web';

import {COLLAPSED_FEATURE_COUNT} from '../../../constants/COLLAPSED_FEATURE_COUNT';
import {PremiumFeature} from '../types/PremiumFeature';
import {ExpandToggle} from './ExpandToggle';
import {VehicleFeature} from './VehicleFeature';

/** Narrowest feature column (in space steps, 44 = 176px); a narrower preview gets one column. */
const FEATURE_COLUMN_MIN_WIDTH = 44;

/** Up to as many columns as fit, each at least the minimal width. */
const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(min(100%, ${({theme}) => theme.getSize(FEATURE_COLUMN_MIN_WIDTH)}), 1fr)
  );
  column-gap: ${({theme}) => theme.getSize(4)};
  row-gap: ${({theme}) => theme.getSize(2)};
`;

interface Feature {
  key: string;
  label: string;
}

interface FeatureListProps extends TestIdProps {
  features: Feature[];
  /** Premium (highlighted) features, the same ones the vehicle header shows. */
  premiumFeatures?: PremiumFeature[];
}

export function FeatureList(props: FeatureListProps) {
  const [isExpanded, , , toggleExpanded] = useBoolean();
  // Auction features are plain strings and may repeat; each is listed once, keyed by itself.
  const premiumFeatures = uniqBy(prop('key'), props.premiumFeatures ?? []);

  if (isEmpty(props.features) && isEmpty(premiumFeatures)) {
    return null;
  }

  // Premium features are listed only once, in their own block above.
  const premiumKeys = premiumFeatures.map((feature) => feature.key);
  const otherFeatures = uniqBy(prop('key'), props.features).filter(
    (feature) => !premiumKeys.includes(feature.key)
  );
  const isCollapsible = otherFeatures.length > COLLAPSED_FEATURE_COUNT;
  const visibleFeatures =
    isCollapsible && !isExpanded ? otherFeatures.slice(0, COLLAPSED_FEATURE_COUNT) : otherFeatures;

  return (
    <Card data-testid={props['data-testid']}>
      <VStack spacing={4}>
        <HStack justify="space-between" align="center" spacing={2}>
          <Heading size={4}>
            {i18n.t('entity.sourcing.vehiclePreview.labels.vehicleFeatures')}
          </Heading>
          <Box
            backgroundColor="palettes.neutral.20.100"
            borderRadius="circular"
            paddingHorizontal={2}
          >
            <Text
              size="xSmall"
              color="secondary"
              alternative
              data-testid={suffixTestId('count', props)}
            >
              {(otherFeatures.length + premiumFeatures.length).toString()}
            </Text>
          </Box>
        </HStack>
        <Show when={isNotEmpty(premiumFeatures)}>
          <VStack spacing={2}>
            <HStack spacing={1} align="center">
              <Icon value="toggle/star" size={4} color="severity.warning" />
              <Text size="xSmall" color="secondary" alternative>
                {i18n.t('entity.vehicle.labels.premium')}
              </Text>
            </HStack>
            <HStack spacing={2} wrap data-testid={suffixTestId('premium', props)}>
              {premiumFeatures.map((feature) => (
                <Tooltip key={feature.key} label={feature.tooltip}>
                  <Box
                    backgroundColor="palettes.blue.20.100"
                    borderRadius="large"
                    paddingHorizontal={3}
                    paddingVertical={1}
                    maxWidth="100%"
                  >
                    <HStack spacing={1} align="center">
                      <Icon
                        value={feature.icon ?? 'action/done'}
                        size={4}
                        color="palettes.blue.80.100"
                      />
                      <Text size="small" color="link" alternative overflowWrap="anywhere">
                        {feature.label}
                      </Text>
                    </HStack>
                  </Box>
                </Tooltip>
              ))}
            </HStack>
          </VStack>
        </Show>
        <Show when={isNotEmpty(premiumFeatures) && isNotEmpty(visibleFeatures)}>
          <Box borderTop="1px solid" borderColor="general.separator" />
        </Show>
        <FeatureGrid>
          {visibleFeatures.map((feature) => (
            <HStack key={feature.key} spacing={2} align="center">
              <Icon value="action/done" size={4} color="severity.success" />
              <Box minWidth={0} flex={1}>
                <VehicleFeature
                  name={feature.label}
                  data-testid={suffixTestId(feature.key, props)}
                />
              </Box>
            </HStack>
          ))}
        </FeatureGrid>
        <Show when={isCollapsible}>
          <ExpandToggle
            isExpanded={isExpanded}
            expandLabel={i18n.t('entity.sourcing.vehiclePreview.actions.showAllFeatures', {
              count: otherFeatures.length,
            })}
            onToggle={toggleExpanded}
            data-testid={suffixTestId('toggle', props)}
          />
        </Show>
      </VStack>
    </Card>
  );
}
